import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, RefreshCw } from 'lucide-react';
import { sessionActivity } from '../lib/session-activity';
import { useCoreAuth } from '../contexts/AuthContext';

export function SessionTimeoutWarning() {
  const { refreshSession } = useCoreAuth();
  const [minutesLeft, setMinutesLeft] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Set up inactivity warning callback
    sessionActivity.onInactivityWarning((minutes) => {
      setMinutesLeft(minutes);
    });

    // Set up timeout callback
    sessionActivity.onSessionTimeout(() => {
      // Session timed out, will be handled by auth state change
      setMinutesLeft(null);
    });

    return () => {
      // Cleanup
      sessionActivity.onInactivityWarning(() => {});
      sessionActivity.onSessionTimeout(() => {});
    };
  }, []);

  const handleContinue = async () => {
    setIsRefreshing(true);
    
    try {
      // Update activity
      sessionActivity.updateActivity();
      
      // Refresh session
      await refreshSession();
      
      // Hide warning
      setMinutesLeft(null);
    } catch (error) {
      console.error('Failed to refresh session:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <AnimatePresence>
      {minutesLeft !== null && minutesLeft > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-4 right-4 z-50 max-w-sm"
        >
          <div className="bg-white rounded-lg shadow-lg border border-orange-200 p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  Session Timeout Warning
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Your session will expire in {minutesLeft} minute{minutesLeft > 1 ? 's' : ''} due to inactivity.
                </p>
                
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={handleContinue}
                    disabled={isRefreshing}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRefreshing ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1.5" />
                        Continue Session
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => setMinutesLeft(null)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}